import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
    Autocomplete,
    Button,
    Card,
    CardContent,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import { API_URL } from "../config";
import SoftwareDownloadLogDialog from "./SoftwareDownloadLogDialog";

function formatBytes(n) {
    const size = Number(n) || 0;
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export default function SoftwareDemosPanel({ customers = [], setMessage, busy, setBusy }) {
    const [items, setItems] = useState([]);
    const [access, setAccess] = useState([]);
    const [title, setTitle] = useState("");
    const [version, setVersion] = useState("");
    const [notes, setNotes] = useState("");
    const [file, setFile] = useState(null);
    const [grantDemoId, setGrantDemoId] = useState("");
    const [grantCustomer, setGrantCustomer] = useState(null);
    const [grantNotes, setGrantNotes] = useState("");
    const [logOpen, setLogOpen] = useState(false);
    const [logLoading, setLogLoading] = useState(false);
    const [logTitle, setLogTitle] = useState("Downloads");
    const [logData, setLogData] = useState({ events: [], total: 0, uniqueCount: 0, anonymousCount: 0 });

    const loadDemos = useCallback(async () => {
        const { data } = await axios.get(`${API_URL}/software-installers/demos`, {
            withCredentials: true,
        });
        setItems(data.items || []);
        setAccess(data.access || []);
    }, []);

    useEffect(() => {
        loadDemos().catch((error) =>
            setMessage?.({
                severity: "error",
                text: error.response?.data?.error || "Unable to load demos.",
            })
        );
    }, [loadDemos, setMessage]);

    async function upload(event) {
        event.preventDefault();
        if (!file) {
            setMessage?.({ severity: "warning", text: "Choose a demo file." });
            return;
        }
        if (!title.trim()) {
            setMessage?.({ severity: "warning", text: "Enter a demo title." });
            return;
        }
        setBusy(true);
        try {
            const body = new FormData();
            body.append("file", file);
            body.append("title", title.trim());
            if (version.trim()) body.append("version", version.trim());
            if (notes.trim()) body.append("notes", notes.trim());
            await axios.post(`${API_URL}/software-installers/demos`, body, {
                withCredentials: true,
                maxContentLength: Infinity,
                maxBodyLength: Infinity,
            });
            setFile(null);
            setTitle("");
            setVersion("");
            setNotes("");
            setMessage?.({ severity: "success", text: "Demo uploaded." });
            await loadDemos();
        } catch (error) {
            setMessage?.({
                severity: "error",
                text: error.response?.data?.error || error.message || "Demo upload failed.",
            });
        } finally {
            setBusy(false);
        }
    }

    async function remove(id) {
        if (!window.confirm("Delete this demo and revoke all customer access?")) return;
        setBusy(true);
        try {
            await axios.delete(`${API_URL}/software-installers/demos/${id}`, { withCredentials: true });
            setMessage?.({ severity: "success", text: "Demo deleted." });
            await loadDemos();
        } catch (error) {
            setMessage?.({
                severity: "error",
                text: error.response?.data?.error || "Could not delete demo.",
            });
        } finally {
            setBusy(false);
        }
    }

    function downloadAdmin(id) {
        window.open(`${API_URL}/software-installers/demos/${id}/download`, "_blank", "noopener,noreferrer");
    }

    async function openLog(row) {
        setLogTitle(`Downloads · ${row.title}`);
        setLogOpen(true);
        setLogLoading(true);
        try {
            const { data } = await axios.get(`${API_URL}/software-installers/demos/${row.id}/downloads`, {
                withCredentials: true,
            });
            setLogData(data);
        } catch (error) {
            setMessage?.({
                severity: "error",
                text: error.response?.data?.error || "Could not load download log.",
            });
            setLogData({ events: [], total: 0, uniqueCount: 0, anonymousCount: 0 });
        } finally {
            setLogLoading(false);
        }
    }

    async function grantAccess(event) {
        event.preventDefault();
        if (!grantDemoId) {
            setMessage?.({ severity: "warning", text: "Pick a demo." });
            return;
        }
        if (!grantCustomer?.id) {
            setMessage?.({ severity: "warning", text: "Pick a customer." });
            return;
        }
        setBusy(true);
        try {
            await axios.put(
                `${API_URL}/software-installers/demos/${grantDemoId}/access`,
                {
                    customerId: grantCustomer.id,
                    email: grantCustomer.email || null,
                    notes: grantNotes.trim() || null,
                },
                { withCredentials: true }
            );
            setGrantCustomer(null);
            setGrantNotes("");
            setMessage?.({
                severity: "success",
                text: "Access enabled. This demo will appear under Software Demos on their PBR account.",
            });
            await loadDemos();
        } catch (error) {
            setMessage?.({
                severity: "error",
                text: error.response?.data?.error || "Could not grant access.",
            });
        } finally {
            setBusy(false);
        }
    }

    async function revokeAccess(accessId) {
        if (!window.confirm("Revoke this customer's demo access?")) return;
        setBusy(true);
        try {
            await axios.delete(`${API_URL}/software-installers/demos/access/${accessId}`, {
                withCredentials: true,
            });
            setMessage?.({ severity: "success", text: "Access revoked." });
            await loadDemos();
        } catch (error) {
            setMessage?.({
                severity: "error",
                text: error.response?.data?.error || "Could not revoke access.",
            });
        } finally {
            setBusy(false);
        }
    }

    return (
        <Stack spacing={2.5}>
            <Card variant="outlined">
                <CardContent>
                    <Stack component="form" spacing={2} onSubmit={upload}>
                        <Stack spacing={0.5}>
                            <Typography variant="subtitle1">Upload software demo</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Demos stay hidden until you enable access for a customer. Their member account then
                                shows a Software Demos category (matched by email).
                            </Typography>
                        </Stack>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="Title"
                                    placeholder="Robot viewer demo"
                                    required
                                    fullWidth
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 3 }}>
                                <TextField
                                    label="Version (optional)"
                                    placeholder="1.0.0"
                                    fullWidth
                                    value={version}
                                    onChange={(e) => setVersion(e.target.value)}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 3 }}>
                                <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} fullWidth sx={{ height: "100%" }}>
                                    {file ? file.name : "Choose file"}
                                    <input
                                        hidden
                                        type="file"
                                        accept=".exe,.zip,.msi,.7z,.dmg,.pkg,.iso"
                                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                                    />
                                </Button>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    label="Notes (optional)"
                                    fullWidth
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </Grid>
                        </Grid>
                        <Stack direction="row" spacing={1}>
                            <Button type="submit" variant="contained" disabled={busy}>
                                Upload demo
                            </Button>
                            <Button
                                variant="text"
                                startIcon={<RefreshIcon />}
                                disabled={busy}
                                onClick={() =>
                                    loadDemos().catch((error) =>
                                        setMessage?.({
                                            severity: "error",
                                            text: error.response?.data?.error || "Refresh failed.",
                                        })
                                    )
                                }
                            >
                                Refresh
                            </Button>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent sx={{ overflowX: "auto" }}>
                    <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                        Demo files
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Title</TableCell>
                                <TableCell>Size</TableCell>
                                <TableCell>Downloads</TableCell>
                                <TableCell>Access</TableCell>
                                <TableCell>Uploaded</TableCell>
                                <TableCell>By</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Typography color="text.secondary">No demos uploaded yet.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                items.map((row) => (
                                    <TableRow key={row.id} hover>
                                        <TableCell>
                                            <Typography fontWeight={700}>{row.title}</Typography>
                                            {row.version ? (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    v{row.version}
                                                </Typography>
                                            ) : null}
                                            {row.notes ? (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {row.notes}
                                                </Typography>
                                            ) : null}
                                        </TableCell>
                                        <TableCell>{formatBytes(row.fileSize)}</TableCell>
                                        <TableCell>
                                            <Button size="small" onClick={() => openLog(row)}>
                                                {row.downloadCount || 0}
                                                {row.uniqueDownloaders
                                                    ? ` · ${row.uniqueDownloaders} people`
                                                    : ""}
                                            </Button>
                                        </TableCell>
                                        <TableCell>{row.accessCount || 0}</TableCell>
                                        <TableCell>
                                            {row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}
                                        </TableCell>
                                        <TableCell>{row.uploadedBy || "—"}</TableCell>
                                        <TableCell align="right">
                                            <IconButton title="Download" onClick={() => downloadAdmin(row.id)} disabled={busy}>
                                                <DownloadIcon />
                                            </IconButton>
                                            <IconButton
                                                title="Delete"
                                                color="error"
                                                onClick={() => remove(row.id)}
                                                disabled={busy}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <Stack spacing={2}>
                        <Stack spacing={0.5}>
                            <Typography variant="subtitle1">Customer access</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Enable a demo for a customer. If their PBR site login email matches, Software Demos
                                appears in their account.
                            </Typography>
                        </Stack>
                        <Stack component="form" spacing={2} onSubmit={grantAccess}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 5 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="grant-demo-label">Demo</InputLabel>
                                        <Select
                                            labelId="grant-demo-label"
                                            label="Demo"
                                            value={grantDemoId}
                                            onChange={(e) => setGrantDemoId(e.target.value)}
                                        >
                                            <MenuItem value="">
                                                <em>Select a demo</em>
                                            </MenuItem>
                                            {items.map((demo) => (
                                                <MenuItem key={demo.id} value={demo.id}>
                                                    {demo.title}
                                                    {demo.version ? ` · v${demo.version}` : ""}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid size={{ xs: 12, md: 7 }}>
                                    <Autocomplete
                                        options={customers}
                                        value={grantCustomer}
                                        onChange={(_e, v) => setGrantCustomer(v)}
                                        getOptionLabel={(c) =>
                                            c ? `${c.name || "Customer"}${c.email ? ` · ${c.email}` : ""}` : ""
                                        }
                                        isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Customer" size="small" />
                                        )}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        label="Notes (optional)"
                                        size="small"
                                        fullWidth
                                        value={grantNotes}
                                        onChange={(e) => setGrantNotes(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                            <Button type="submit" variant="contained" disabled={busy} sx={{ alignSelf: "flex-start" }}>
                                Enable access
                            </Button>
                        </Stack>

                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Demo</TableCell>
                                    <TableCell>Customer</TableCell>
                                    <TableCell>Enabled</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {access.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4}>
                                            <Typography color="text.secondary">No customers have demo access yet.</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    access.map((row) => (
                                        <TableRow key={row.id} hover>
                                            <TableCell>{row.demoTitle || row.demoId}</TableCell>
                                            <TableCell>
                                                <Typography fontWeight={600}>
                                                    {row.customerName || row.customerId}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {row.email || "—"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    title="Revoke"
                                                    color="error"
                                                    onClick={() => revokeAccess(row.id)}
                                                    disabled={busy}
                                                >
                                                    <DeleteOutlineIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </Stack>
                </CardContent>
            </Card>

            <SoftwareDownloadLogDialog
                open={logOpen}
                onClose={() => setLogOpen(false)}
                title={logTitle}
                loading={logLoading}
                total={logData.total}
                uniqueCount={logData.uniqueCount}
                anonymousCount={logData.anonymousCount}
                events={logData.events}
            />
        </Stack>
    );
}
